import { LeadRejectionReason, useUpdateLeadStatusMutation } from '@/services/leads';
import { Box, Button, Modal, SelectInputNative, toast } from '@palmetto/palmetto-components';
import { useCallback, useState } from 'react';

import { isErrorWithData } from '../../services/helpers';

interface LeadManagementRejectLeadModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  leadId: string;
  location: string;
}

const REJECTION_REASON_OPTIONS: { value: LeadRejectionReason; label: string }[] = [
  { value: 'NOT_INTERESTED', label: "I'm not interested" },
  { value: 'CAPACITY', label: "I don't have capacity but I'm open to seeing this lead again later." },
];

export function LeadManagementRejectLeadModal({
  isOpen,
  onDismiss,
  onConfirm,
  leadId,
  location,
}: LeadManagementRejectLeadModalProps) {
  const [reason, setReason] = useState<LeadRejectionReason | ''>('');
  const [updateLeadStatus, { isLoading }] = useUpdateLeadStatusMutation();

  const handleReasonChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setReason(e.target.value as LeadRejectionReason);
  }, []);

  const handleDismiss = useCallback(() => {
    setReason('');
    onDismiss();
  }, [onDismiss]);

  const handleConfirm = useCallback(async () => {
    if (!reason) return;
    try {
      await updateLeadStatus({ leadId, status: 'REJECTED', rejectionReason: reason }).unwrap();
      toast.success(
        reason === 'CAPACITY'
          ? "Lead rejected. We'll show you the lead later if it doesn't get picked up."
          : 'Lead rejected.',
      );
      setReason('');
      onConfirm();
    } catch (e) {
      if (isErrorWithData(e)) {
        toast.error(`Error rejecting lead: ${e.data.message}`);
      } else {
        console.error(e);
        toast.error('Error rejecting lead');
      }
    }
  }, [leadId, reason, updateLeadStatus, onConfirm]);
  return (
    <Modal isOpen={isOpen} onDismiss={handleDismiss} ariaLabelledBy="rejectLeadModal" maxWidth="4xl">
      <Modal.Header
        id="rejectLeadModal"
        title={
          (
            <>
              Are you sure you want to reject: <br />
              {location}?
            </>
          ) as unknown as string
        }
        onDismiss={handleDismiss}
      />
      <Modal.Body background="secondary" childGap="md">
        <SelectInputNative
          id="rejectionReason"
          name="rejectionReason"
          label="Choose reason for rejection"
          isRequired
          value={reason}
          onChange={handleReasonChange}
          options={REJECTION_REASON_OPTIONS}
          placeholder="Choose reason for rejection"
        />
      </Modal.Body>
      <Modal.Footer>
        <Box direction="row" childGap="sm" justifyContent="space-between" width="100">
          <Button variant="secondary" tone="neutral" type="button" onClick={handleDismiss} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            tone="danger"
            type="button"
            onClick={handleConfirm}
            isDisabled={!reason || isLoading}
            isLoading={isLoading}
          >
            Confirm rejection
          </Button>
        </Box>
      </Modal.Footer>
    </Modal>
  );
}
